import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Box, Button, createStyles, Group, Text, Tooltip} from '@mantine/core';
import React, {useCallback} from 'react';

import {AgentApprovalResponse} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as ChatActions from 'llm/chat-library/ChatActions';
import {buildAgentApprovalDecision} from 'llm/chat-library/ChatDAO';
import {ChatDraftStatus, getOrCreateDraftState} from 'llm/chat-library/ChatDraftState';

/** Explanation of what "actions" means, shown in the info tooltip on the approval card. */
const APPROVAL_ACTIONS_TOOLTIP =
  'Actions are write actions that require your approval — for example registering entities, ' +
  'transferring material into containers, creating plates, or editing items. Approving them ' +
  'applies for the rest of this chat.';

/** Tooltip on the "Deny" button explaining that a typed reply is an alternative to denying. */
const DENY_TOOLTIP = 'Click to deny, or send a message to explain what to do instead.';

const useStyles = createStyles((theme) => ({
  // A distinct, system-owned card that sits at the bottom of the agent's approval-request message,
  // inside its border. The different background separates it from the agent's message content.
  card: {
    marginTop: theme.spacing.md,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.colors.blue[0],
    border: `1px solid ${theme.colors.blue[2]}`,
  },
}));

/**
 * The distinct system card appended to the bottom of an assistant write-approval request message.
 *
 * Unless readonly, it shows an "Allow actions for this chat" button and a gray "Deny" button, plus
 * an info tooltip explaining what "actions" means. Clicking "Allow" submits the user's current
 * draft (any typed text/attachments, usually none) carrying a granting AgentApprovalDecision
 * (WRITE_ACTIONS / APPROVED_THIS_CHAT), which approves the gated write actions for the rest of the
 * chat. Clicking "Deny" submits the current draft carrying an EXPLICITLY_DENIED decision. A normal
 * typed reply (without clicking a button) is non-granting and lets the user refine or decline the plan.
 */
export default function AgentApprovalCard({
  chatSessionId,
  requestEventId,
  readonly,
}: {
  chatSessionId: string;
  /**
   * ID of the assistant display message chat event this card belongs to (it carried the approval
   * request). Recorded on the submitted decision so it links back to the request it answers.
   */
  requestEventId: string;
  readonly: boolean;
}): JSX.Element {
  const {classes} = useStyles();
  const submitDecision = useCallback(
    (approvalResponse: AgentApprovalResponse) => {
      const draft = getOrCreateDraftState(chatSessionId);
      // Submit whatever the user has typed (if anything) alongside the decision, rather than
      // discarding their in-progress draft. Usually the composer is empty and only the decision is
      // recorded on the user message.
      ChatActions.submitDraftMessage(chatSessionId, {
        draft: {
          ...draft,
          status: ChatDraftStatus.DRAFT,
        },
        createParams: {
          agentApprovalDecision: buildAgentApprovalDecision(approvalResponse, requestEventId),
        },
      });
    },
    [chatSessionId, requestEventId]
  );
  const handleApprove = useCallback(() => {
    submitDecision(AgentApprovalResponse.APPROVED_THIS_CHAT);
  }, [submitDecision]);
  const handleDeny = useCallback(() => {
    submitDecision(AgentApprovalResponse.EXPLICITLY_DENIED);
  }, [submitDecision]);

  return (
    <Box className={classes.card} {...getTestProps({component: 'AgentApprovalCard', element: 'root'})}>
      <Group position="apart" spacing="xs" noWrap={true}>
        <Group spacing={6} noWrap={true}>
          <Text size="sm" weight={600}>
            Approval required
          </Text>
          <Tooltip label={APPROVAL_ACTIONS_TOOLTIP} multiline={true} width={280} withArrow={true}>
            <ActionIcon
              size="xs"
              radius="xl"
              variant="light"
              color="blue"
              aria-label="What are actions?"
              {...getTestProps({component: 'AgentApprovalCard', element: 'info'})}
            >
              <Text size="xs" weight={700}>
                ?
              </Text>
            </ActionIcon>
          </Tooltip>
        </Group>
        {!readonly && (
          <Group spacing="xs" noWrap={true}>
            <Tooltip label={DENY_TOOLTIP} multiline={true} width={240} withArrow={true}>
              <Button
                size="xs"
                variant="tertiary"
                onClick={handleDeny}
                {...getTestProps({component: 'AgentApprovalCard', element: 'deny'})}
              >
                Deny
              </Button>
            </Tooltip>
            <Button
              size="xs"
              variant="primary"
              onClick={handleApprove}
              {...getTestProps({component: 'AgentApprovalCard', element: 'approve'})}
            >
              Allow actions for this chat
            </Button>
          </Group>
        )}
      </Group>
    </Box>
  );
}
